import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  RefreshCw,
  Database,
  Heart,
  Cpu,
  ServerCog,
} from 'lucide-react';
import { systemAPI, ProbeResult, ProbeStatus } from '@/services/system-api';
import { auditAPI } from '@/services/audit-api';

const POLL_INTERVAL_MS = 30_000;

const STATUS_TONE: Record<ProbeStatus, string> = {
  ok: 'bg-[var(--lib-success)]/10 text-[var(--lib-success)] border-[var(--lib-success)]/30',
  degraded: 'bg-[var(--lib-gold)]/10 text-[var(--lib-gold-text)] border-[var(--lib-gold)]/30',
  down: 'bg-[var(--lib-danger)]/10 text-[var(--lib-danger)] border-[var(--lib-danger)]/30',
};

const STATUS_DOT: Record<ProbeStatus, string> = {
  ok: 'bg-[var(--lib-success)]',
  degraded: 'bg-[var(--lib-gold)]',
  down: 'bg-[var(--lib-danger)]',
};

const STATUS_LABEL: Record<ProbeStatus, string> = {
  ok: 'Operational',
  degraded: 'Degraded',
  down: 'Down',
};

const PROBE_ICON: Record<string, React.ComponentType<{ className?: string }>> = {
  Application: Heart,
  Liveness: Activity,
  Readiness: Database,
  Metrics: Cpu,
};

const fmtLatency = (ms: number | null) => (ms == null ? '—' : `${ms} ms`);

const SystemModule: React.FC = () => {
  const [probes, setProbes] = useState<ProbeResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [authMetrics, setAuthMetrics] = useState<Record<string, unknown> | null>(null);
  const [rateMetrics, setRateMetrics] = useState<Record<string, unknown> | null>(null);
  const intervalRef = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [results, auth, rate] = await Promise.all([
        systemAPI.probeAll(),
        auditAPI.authMetrics(24).catch(() => null),
        auditAPI.rateLimitMetrics(24).catch(() => null),
      ]);
      setProbes(results);
      setAuthMetrics(auth);
      setRateMetrics(rate);
      setLastChecked(new Date());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!autoRefresh) {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }
    intervalRef.current = window.setInterval(() => {
      void refresh();
    }, POLL_INTERVAL_MS);
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, refresh]);

  const overall: ProbeStatus = useMemo(() => {
    if (probes.length === 0) return 'down';
    if (probes.some((p) => p.status === 'down')) return 'down';
    if (probes.some((p) => p.status === 'degraded')) return 'degraded';
    return 'ok';
  }, [probes]);

  const avgLatency = useMemo(() => {
    const valid = probes.filter((p) => p.latency_ms != null) as Array<ProbeResult & { latency_ms: number }>;
    if (valid.length === 0) return null;
    return Math.round(valid.reduce((sum, p) => sum + p.latency_ms, 0) / valid.length);
  }, [probes]);

  const dbHealthy = probes.find((p) => p.endpoint === '/ready/')?.status === 'ok';

  return (
    <div className="space-y-5 max-w-[1400px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-[22px] font-bold text-[var(--lib-ink)] flex items-center gap-2">
            <ServerCog className="h-6 w-6 text-[var(--lib-gold)]" />
            System Health
          </h1>
          <p className="text-sm text-[var(--lib-ink-muted)] mt-1">
            Live status of the libgravity-api backend probes.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-[var(--lib-ink-muted)] cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 rounded border-[var(--lib-hairline)] text-[var(--lib-gold-text)] focus:ring-[var(--lib-gold)]"
            />
            Auto-refresh (30s)
          </label>
          <button
            onClick={() => void refresh()}
            disabled={loading}
            className="border border-[var(--lib-gold-text)] text-[var(--lib-gold-text)] hover:bg-[var(--lib-surface-2)] font-medium text-sm rounded-md px-4 py-2 transition-colors flex items-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Overall banner */}
      <div className={`border rounded-lg px-6 py-5 flex items-center gap-4 ${STATUS_TONE[overall]}`}>
        <OverallIcon status={overall} />
        <div className="flex-1">
          <div className="font-semibold text-lg">
            {overall === 'ok'
              ? 'All systems operational'
              : overall === 'degraded'
                ? 'Some services degraded'
                : 'One or more services down'}
          </div>
          <div className="text-xs font-mono uppercase tracking-wider opacity-75 mt-0.5">
            {lastChecked
              ? `Last checked · ${lastChecked.toLocaleTimeString()}`
              : 'Awaiting first check'}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display text-[28px] font-bold">{fmtLatency(avgLatency)}</div>
          <div className="text-xs font-mono uppercase tracking-wider opacity-75">Avg latency</div>
        </div>
      </div>

      {/* Probe grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {probes.map((p) => {
          const Icon = PROBE_ICON[p.name] ?? Activity;
          return (
            <div key={p.endpoint} className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4 text-[var(--lib-ink-muted)]" />
                  <span className="text-sm font-semibold text-[var(--lib-ink)]">{p.name}</span>
                </div>
                <span className={`relative flex h-2.5 w-2.5`}>
                  {p.status === 'ok' && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--lib-success)] opacity-60" />
                  )}
                  <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${STATUS_DOT[p.status]}`} />
                </span>
              </div>
              <div className="mt-4 flex items-end gap-3">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS_TONE[p.status]}`}>
                  {STATUS_LABEL[p.status]}
                </span>
                <span className="ml-auto text-xs font-mono text-[var(--lib-ink-muted)]">{fmtLatency(p.latency_ms)}</span>
              </div>
              <div className="mt-3 pt-3 border-t border-[var(--lib-hairline)] space-y-1">
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--lib-ink-muted)]">Endpoint</div>
                <div className="font-mono text-xs text-[var(--lib-ink-muted)] truncate">{p.endpoint}</div>
                {p.http_status != null && (
                  <div className="text-xs text-[var(--lib-ink-muted)] mt-1">
                    HTTP <span className="font-mono">{p.http_status}</span>
                    {p.data && (() => {
                      const meaningful = Object.entries(p.data).find(
                        ([key]) => key !== 'status' && typeof p.data?.[key] !== 'object',
                      );
                      if (meaningful) {
                        return (
                          <span className="ml-2 font-mono text-[var(--lib-ink-muted)]">
                            · {meaningful[0]}: {String(meaningful[1])}
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </div>
                )}
                {p.error && <div className="text-xs text-[var(--lib-danger)] mt-1 truncate">{p.error}</div>}
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <DetailCard
          icon={Database}
          title="Database"
          value={dbHealthy ? 'Connected' : 'Unreachable'}
          tone={dbHealthy ? 'ok' : 'down'}
          hint="Determined by the readiness probe (/ready/)."
        />
        <DetailCard
          icon={Heart}
          title="Auth (24h)"
          value={extractMetric(authMetrics, ['successful_logins', 'success', 'count']) ?? '—'}
          subtitle={
            extractMetric(authMetrics, ['failed_logins', 'failed']) != null
              ? `${extractMetric(authMetrics, ['failed_logins', 'failed'])} failures`
              : undefined
          }
          tone="ok"
          hint="Successful logins from /security/metrics/authentication/."
        />
        <DetailCard
          icon={Cpu}
          title="Rate limits (24h)"
          value={extractMetric(rateMetrics, ['total_violations', 'count', 'total']) ?? '—'}
          subtitle={
            extractMetric(rateMetrics, ['blocked_count', 'blocked']) != null
              ? `${extractMetric(rateMetrics, ['blocked_count', 'blocked'])} blocked`
              : undefined
          }
          tone="ok"
          hint="Violations from /security/metrics/rate_limiting/."
        />
      </div>

      <p className="font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">
        Probes hit /api/v1/health/, /live/, /ready/, /metrics/ ·{' '}
        {autoRefresh ? `auto-refresh every ${POLL_INTERVAL_MS / 1000}s` : 'auto-refresh paused'}
      </p>
    </div>
  );
};

const OverallIcon: React.FC<{ status: ProbeStatus }> = ({ status }) => {
  const Icon = status === 'ok' ? CheckCircle2 : status === 'degraded' ? AlertTriangle : XCircle;
  return <Icon className="h-7 w-7" />;
};

const DetailCard: React.FC<{
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string | number;
  subtitle?: string;
  tone: ProbeStatus;
  hint?: string;
}> = ({ icon: Icon, title, value, subtitle, tone, hint }) => (
  <div className="bg-[var(--lib-surface)] border border-[var(--lib-hairline)] rounded-lg p-5">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)]">
        <Icon className="h-3.5 w-3.5" />
        {title}
      </div>
      <span className={`h-2 w-2 rounded-full ${STATUS_DOT[tone]}`} />
    </div>
    <div className="mt-3 font-display text-[28px] font-bold text-[var(--lib-ink)]">{value}</div>
    {subtitle && <div className="text-xs text-[var(--lib-ink-muted)] mt-0.5">{subtitle}</div>}
    {hint && <div className="text-[11px] text-[var(--lib-ink-muted)] mt-3">{hint}</div>}
  </div>
);

function extractMetric(
  payload: Record<string, unknown> | null,
  keys: string[],
): string | number | null {
  if (!payload) return null;
  for (const key of keys) {
    const v = payload[key];
    if (typeof v === 'number') return v.toLocaleString();
    if (typeof v === 'string') return v;
  }
  // Walk one level deep into nested dicts (metrics endpoints often nest by category)
  for (const value of Object.values(payload)) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = extractMetric(value as Record<string, unknown>, keys);
      if (nested != null) return nested;
    }
  }
  return null;
}

export default SystemModule;
